import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { UserDTO } from '@nexply/api-client';
import { api } from './api.js';

interface AuthState {
  user: UserDTO | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const me = await api.auth.me();
      setUser(me.user);
    } catch {
      setUser(null);
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const value: AuthState = {
    user,
    loading,
    refresh,
    login: async (email, password) => {
      const res = await api.auth.login({ email, password });
      setUser(res.user);
    },
    register: async (email, password, firstName, lastName) => {
      // Die UI erzwingt die Checkbox; der Contract verlangt das Feld explizit.
      const res = await api.auth.register({ email, password, firstName, lastName, acceptTerms: true });
      setUser(res.user);
    },
    logout: async () => {
      await api.auth.logout();
      setUser(null);
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be inside AuthProvider');
  return v;
}
